import { useEffect } from "react";
import imgHeroBg from "../../assets/new_ui_img/contactus_banner.jpg";
import { Header, Footer, ScrollToTop } from "./components/Header";

function useReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.07, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".cu-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <div className="pt-20">
      <div className="relative w-full overflow-hidden" style={{ height: "140px" }}>
        {/* Background map image */}
        <div className="absolute inset-0 hero-bg-animate" style={{ transformOrigin: "center top" }}>
          <img alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" src={imgHeroBg} />          
        </div>       
        {/* White overlay */}
        <div className="absolute inset-0 bg-[rgba(255,255,255,0.8)]" />
        {/* Title */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 h-full flex items-center">
          <p className="hero-title-animate font-montserrat font-bold text-black text-[clamp(1.4rem,3.2vw,2.75rem)] leading-[1.3]">
            {"Let's Start the Conversation"}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Contact Info ─────────────────────────────────────────────────────────────
function ContactInfo() {
  return (
    <div className="flex flex-col gap-[clamp(24px,3vw,36px)]">
      {/* Heading */}
      <div className="flex flex-col gap-[12px]">
        <p className="font-google-sans font-medium text-black text-[clamp(1.6rem,2.5vw,2.5rem)] leading-[1.3]"
          style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
          Contact Us
        </p>
        <p className="font-lato text-[#6b6b6b] text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3]">
          {"We're here to support your journey into the future of parking."}
        </p>
      </div>

      {/* Address */}
      <div className="flex flex-col gap-[8px]">
        <p className="font-lato text-[#8d8d8d] text-[13px] uppercase tracking-wide">Address</p>
        <a
          href="https://www.bing.com/maps?q=800+Bellevue+Way+NE,+Suite+%23500,+Bellevue,+WA+98004,+USA"
          target="_blank"
          rel="noopener noreferrer"
          className="font-lato text-[#0a66c2] hover:underline text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.5]"
        >
          GeoParking Technologies, Inc.<br />
          800 Bellevue Way NE, Suite #500<br />
          Bellevue, WA 98004, U.S.A
        </a>
      </div>

      {/* Phone */}
      <div className="flex flex-col gap-[8px]">
        <p className="font-lato text-[#8d8d8d] text-[13px] uppercase tracking-wide">Phone</p>
        <p className="font-lato text-[#121212] text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3]">+1 (425) 459-8400</p>
      </div>

      {/* Email */}
      <div className="flex flex-col gap-[8px]">
        <p className="font-lato text-[#8d8d8d] text-[13px] uppercase tracking-wide">Email</p>
        <a href="mailto:support@geoparking.com"
          className="font-lato text-[#0a66c2] text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3] hover:underline">
          support@geoparking.com
        </a>
      </div>

      {/* Website */}
      <div className="flex flex-col gap-[8px]">
        <p className="font-lato text-[#8d8d8d] text-[13px] uppercase tracking-wide">Website</p>
        <a href="https://www.geoparking.com" target="_blank" rel="noopener noreferrer"
          className="font-lato text-[#0a66c2] text-[clamp(1rem,1.2vw,1.25rem)] leading-[1.3] hover:underline">
          www.geoparking.com
        </a>
      </div>
    </div>
  );
}

// ─── Contact Us Page ──────────────────────────────────────────────────────────
export default function ContactUsPage() {
  useReveal();

  const msFormUrl = process.env.REACT_APP_MICROSOFT_FORM_URL || "https://forms.office.com/Pages/ResponsePage.aspx?id=HpcpSB8wUUevSSWu4m0L7_pygkq7OmpKpXsPexQ2DdxUM1FRMTQ0M1ZKWUdUV09ZS0hFRjlTRjNOUy4u&embed=true";

  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <section className="bg-[#f5f5f5] py-[clamp(40px,6vw,80px)]">
          <div className="max-w-[1400px] mx-auto px-6">
            <div className="cu-reveal flex flex-col lg:flex-row gap-[clamp(40px,5vw,70px)] items-start">
              {/* Left: Contact Info */}
              <div className="w-full lg:w-[38%] flex-shrink-0">
                <ContactInfo />
              </div>

              {/* Right: Embedded Verified Microsoft 365 Support Form */}
              <div className="w-full lg:w-[62%]">
                <div className="w-full rounded-[12px] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.07)] bg-white border border-[#e2e8f0]">
                  <iframe
                    title="GeoParking Support & Contact Form"
                    src={msFormUrl}
                    width="100%"
                    height="760px"
                    allowFullScreen
                    style={{ border: 0, display: "block", background: "#ffffff" }}
                  />
                </div>
                <div className="mt-3 text-xs text-[#6b6b6b] text-center">
                  Trouble viewing?{" "}
                  <a
                    href="https://forms.office.com/Pages/ResponsePage.aspx?id=HpcpSB8wUUevSSWu4m0L7_pygkq7OmpKpXsPexQ2DdxUM1FRMTQ0M1ZKWUdUV09ZS0hFRjlTRjNOUy4u"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-[#0a66c2] font-semibold"
                  >
                    Open the form in a new tab
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}
